import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const MEMORY_TYPES = [
  { key: 'PHOTO', label: 'Photo', icon: 'photo', color: 'bg-blue-50 border-blue-300 text-blue-700' },
  { key: 'STORY', label: 'Story', icon: 'book', color: 'bg-green-50 border-green-300 text-green-700' },
  { key: 'AUDIO', label: 'Audio', icon: 'audio', color: 'bg-purple-50 border-purple-300 text-purple-700' },
  { key: 'VIDEO', label: 'Video', icon: 'video', color: 'bg-red-50 border-red-300 text-red-700' },
];

function AddMemoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedMember = searchParams.get('member') || '';

  const [memoryType, setMemoryType] = useState('PHOTO');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [tags, setTags] = useState('');
  const [taggedMembers, setTaggedMembers] = useState(preselectedMember ? [preselectedMember] : []);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      if (selected.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreview(ev.target.result);
        reader.readAsDataURL(selected);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('border-indigo-400', 'bg-indigo-50');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-50');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-50');
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (droppedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreview(ev.target.result);
        reader.readAsDataURL(droppedFile);
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (memoryType === 'PHOTO' && !file && !filePreview) newErrors.file = 'Please upload a photo';
    if (memoryType === 'STORY' && !description.trim()) newErrors.description = 'Story content is required';
    if (memoryType === 'AUDIO' && !file) newErrors.file = 'Please upload an audio file';
    if (memoryType === 'VIDEO' && !file) newErrors.file = 'Please upload a video file';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // TODO: GraphQL mutation
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1500);
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AppIcon name="shield" size={60} className="text-green-500" />
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Memory Saved!</h2>
          <p className="mt-2 text-gray-600">Your {memoryType.toLowerCase()} has been added to the family collection.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button onClick={() => navigate('/memories')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              View Memories
            </button>
            <button onClick={() => { setSubmitted(false); setTitle(''); setDescription(''); setFile(null); setFilePreview(null); }} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              Add Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentType = MEMORY_TYPES.find((t) => t.key === memoryType);

  return (
    <div className="">

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Memory Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">What type of memory?</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {MEMORY_TYPES.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => setMemoryType(type.key)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    memoryType === type.key
                      ? type.color + ' border-current shadow-sm'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <AppIcon name={type.icon} size={24} />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* File Upload (for Photo, Audio, Video) */}
          {(memoryType === 'PHOTO' || memoryType === 'AUDIO' || memoryType === 'VIDEO') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {memoryType === 'PHOTO' ? 'Photo' : memoryType === 'AUDIO' ? 'Audio File' : 'Video File'} *
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept={memoryType === 'PHOTO' ? 'image/*' : memoryType === 'AUDIO' ? 'audio/*' : 'video/*'}
                  onChange={handleFileChange}
                />
                {filePreview ? (
                  <div>
                    <img src={filePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                    <p className="mt-2 text-sm text-gray-500">Click or drag to replace</p>
                  </div>
                ) : file ? (
                  <div>
                    <AppIcon name={currentType.icon} size={36} />
                    <p className="mt-2 text-sm font-medium text-gray-700">{file.name}</p>
                    <p className="text-xs text-gray-500">Click or drag to replace</p>
                  </div>
                ) : (
                  <div>
                    <AppIcon name="folder" size={36} />
                    <p className="mt-2 text-sm text-gray-600">Drag & drop or click to upload</p>
                    <p className="text-xs text-gray-400">
                      {memoryType === 'PHOTO' ? 'JPG, PNG, GIF' : memoryType === 'AUDIO' ? 'MP3, WAV, M4A' : 'MP4, MOV, AVI'}
                    </p>
                  </div>
                )}
              </div>
              {errors.file && <p className="text-xs text-red-600 mt-1">{errors.file}</p>}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give this memory a title..."
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title}</p>}
          </div>

          {/* Description / Story Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {memoryType === 'STORY' ? 'Story Content *' : 'Description'}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={memoryType === 'STORY' ? 'Write your story here...' : 'Add a description...'}
              rows={memoryType === 'STORY' ? 8 : 3}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
          </div>

          {/* Date and Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Where was this?"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Tag People */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tag People</label>
            <input
              type="text"
              placeholder="Search family members..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {taggedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {taggedMembers.map((m) => (
                  <span key={m} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                    Member #{m}
                    <button type="button" onClick={() => setTaggedMembers(taggedMembers.filter((id) => id !== m))} className="text-indigo-400 hover:text-indigo-600">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="#vacation #wedding #birthday..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
            <div className="flex gap-3">
              {[
                { key: 'FAMILY', label: 'Family Only', icon: 'privacy' },
                { key: 'PUBLIC', label: 'Public', icon: 'globe' },
              ].map((v) => (
                <label key={v.key} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="visibility" value={v.key} defaultChecked={v.key === 'FAMILY'} className="text-indigo-600" />
                  <span className="text-sm">{v.icon} {v.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Memory'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default AddMemoryPage;